import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

/**
 * Which paywall offer the web funnel's Pro card makes, decided by a PostHog
 * multivariate feature flag so the experiment can be run from the PostHog UI
 * without a deploy. The arms themselves are priced by the backend
 * (core/config.py FUNNEL_OFFER_VARIANTS), the page only names the arm.
 *
 *   control — $7.99 for the first month
 *   lower   — $4.99 for the first month
 *   pwyw    — pay what you want: the parent names the first month's price
 *
 * The first year is the same in every arm. The flag in PostHog: key
 * `funnel-paywall-offer`, variants named exactly as above.
 *
 * The flag is given until a price is about to be shown (`priceShown`: the
 * funnel has reached the paywall), not a fixed second: PostHog's first flag
 * response on a phone in an ad's in-app browser has been seen to take longer
 * than that, and a visit timed out on the first screen used to reach the
 * paywall three minutes later still out of the test (2026-10-24). The paywall
 * is many screens in, so in practice the answer is known long before it.
 *
 * Anything that isn't an arm the flag dealt this visit (the flag off or
 * answering something else, PostHog not running, or no answer by the time the
 * paywall shows) is "not in the test": the card shows the funnel's own offer
 * (control's price), checkout names no arm, and the page keeps the visit out
 * of the experiment's exposures (FunnelAuthPage: `offer_in_test`). It is never
 * reported as control. A visit decided out stays out even when the flags land
 * later, so the price never changes under the parent.
 *
 * Pinned in sessionStorage once decided ('none' for not in the test): the
 * Google round trip reloads the page and sign-in can re-bucket the visitor,
 * and a parent must never see the price change under them. There is no URL
 * override: the flag (or the pinned session value) is the only way into an
 * arm, so a shared link can never pick a price.
 *
 * A visitor from a Google ad is in the test like anyone else: the ad terms
 * on their first screen are said as "$7.99 or less" (FunnelAuthPage
 * GOOGLE_LANDING_TERMS), which every arm's first month satisfies.
 */

export const OFFER_FLAG_KEY = 'funnel-paywall-offer';
export const OFFER_VARIANTS = ['control', 'lower', 'pwyw'];
export const OFFER_VARIANT_KEY = 'rc_funnel_offer_variant';
const OUT = 'none';   // pinned for "not in the test"

const asVariant = (value) => (OFFER_VARIANTS.includes(value) ? value : null);

// '' = pinned out of the test, an arm, or null when nothing is pinned
const readPinned = () => {
  try {
    const value = sessionStorage.getItem(OFFER_VARIANT_KEY);
    return value === OUT ? '' : asVariant(value);
  } catch (_) {
    return null;
  }
};

const pin = (variant) => {
  try {
    sessionStorage.setItem(OFFER_VARIANT_KEY, variant || OUT);
  } catch (_) {
    // not pinned across a reload; the flag decides again
  }
};

// Reading the flag is what has posthog-js emit the `$feature_flag_called`
// event; only ever called while this visit can still join the test
const readFlag = () => {
  try {
    return asVariant(posthog.getFeatureFlag(OFFER_FLAG_KEY));
  } catch (_) {
    return null;
  }
};

const flagsReady = () => {
  try {
    return !!posthog.featureFlags?.hasLoadedFlags;
  } catch (_) {
    return false;
  }
};

/**
 * @param {boolean} priceShown True once the funnel is showing (or about to
 *   show) a price: the paywall and the steps after it. An undecided visit is
 *   settled out of the test in that very render, so no price is ever shown
 *   from a half answer.
 * @returns {string|null} The arm ('control' | 'lower' | 'pwyw'), '' when this
 *   visit is not in the test, or null while PostHog's first flag response is
 *   still awaited (only ever before the paywall).
 */
export const useOfferVariant = (priceShown = false) => {
  const [variant, setVariant] = useState(() => {
    const pinned = readPinned();
    if (pinned !== null) return pinned;
    if (!posthog.__loaded) {
      pin('');
      return '';
    }
    const now = readFlag();
    if (now || flagsReady()) {
      pin(now || '');
      return now || '';
    }
    return null;
  });

  // Still undecided: take the flags whenever they land, for as long as no
  // price has been shown
  useEffect(() => {
    if (variant !== null) return undefined;
    let settled = false;
    const settle = (next) => {
      if (settled) return;
      settled = true;
      pin(next);
      setVariant(next);
    };
    let unsubscribe = () => {};
    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        if (settled) return;   // decided meanwhile (the paywall came first)
        settle(readFlag() || '');
      }) || unsubscribe;
    } catch (_) {
      settle('');
    }
    return () => {
      settled = true;
      try {
        unsubscribe();
      } catch (_) {
        // ignore
      }
    };
  }, [variant]);

  // A price is about to be shown and the flags still haven't answered: not
  // in the test, decided in this render (React re-runs it before anything is
  // committed) so the paywall and the events it reports never see null. The
  // pin keeps a late answer from changing the price.
  if (variant === null && priceShown) {
    pin('');
    setVariant('');
    return '';
  }
  return variant;
};

export default useOfferVariant;
